import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, ClockCountdown, Package, ArrowRight } from "@phosphor-icons/react";
import PortalLayout from "@/components/PortalLayout";
import { OpBadge, PayBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { naira, fmtDate, CATEGORY_LABEL } from "@/lib/format";

export default function CustomerDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/my/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <PortalLayout><div className="text-slate-500 py-20 text-center">Loading…</div></PortalLayout>;

  const { summary, orders, customer } = data;

  return (
    <PortalLayout>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Welcome, {customer?.company_name}</h1>
          <p className="mt-1 text-slate-500">
            Account type: <span className="font-medium text-slate-700">{CATEGORY_LABEL[customer?.payment_category] || customer?.payment_category}</span>
          </p>
        </div>
      </div>

      {/* Outstanding balance — highest contrast widget */}
      <div className="mt-6 grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-lg bg-[#0A4F35] text-white p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-emerald-100/70 text-xs uppercase tracking-widest">Total Outstanding Balance</p>
            <p className="font-display text-4xl font-extrabold mt-1" data-testid="dash-outstanding">{naira(summary.total_outstanding)}</p>
            {summary.amount_due_now > 0 && (
              <p className="mt-1 text-[#E5B800] font-semibold" data-testid="dash-due-now">Due now: {naira(summary.amount_due_now)}</p>
            )}
          </div>
          {orders.some((o) => o.financials.outstanding > 0) && (
            <Link to={`/portal/orders/${orders.find((o) => o.financials.outstanding > 0)?.id}`}>
              <Button data-testid="dash-pay-btn" className="bg-[#E5B800] hover:bg-[#CCA300] text-black font-bold h-11">Pay Balance</Button>
            </Link>
          )}
        </div>
        <div className="rounded-lg border border-[#E5E7EB] bg-white p-6">
          <div className="flex items-center gap-2 text-slate-400"><Package size={18} /><span className="text-xs uppercase tracking-widest">Active Orders</span></div>
          <p className="font-display text-3xl font-bold text-slate-900 mt-1">{summary.active_orders}</p>
          <p className="text-sm text-slate-500 mt-1">{summary.total_orders} total</p>
        </div>
      </div>

      {/* Orders */}
      <h2 className="mt-10 font-display text-xl font-bold text-slate-900">Your Orders</h2>
      {orders.length === 0 ? (
        <div className="mt-4 rounded-lg border border-dashed border-[#E5E7EB] p-10 text-center">
          <p className="text-slate-500">No orders yet.</p>
          <Link to="/request-fuel"><Button className="mt-4 bg-[#0A4F35] hover:bg-[#083D29] text-white">Request Fuel</Button></Link>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {orders.map((o) => (
            <Link key={o.id} to={`/portal/orders/${o.id}`} data-testid={`order-row-${o.order_number}`}
              className="block rounded-lg border border-[#E5E7EB] bg-white p-5 hover:border-[#0A4F35]/40 transition-colors">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="font-display font-bold text-slate-900">{o.order_number}</span>
                    <OpBadge status={o.operational_status} />
                  </div>
                  <p className="mt-1 text-sm text-slate-500 capitalize">
                    {Number(o.litres).toLocaleString()} L {o.fuel_type} · {fmtDate(o.created_at)}
                  </p>
                </div>
                <div className="text-right">
                  <PayBadge status={o.financials.payment_status} />
                  <p className="mt-1 font-semibold text-slate-900">{naira(o.financials.total)}</p>
                  {o.financials.outstanding > 0 && (
                    <p className="text-sm text-[#DC2626] font-medium">Outstanding {naira(o.financials.outstanding)}</p>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </PortalLayout>
  );
}
